/* eslint-disable react/destructuring-assignment */
import React, { Component } from 'react';
import { kebabCase, isEqual } from 'lodash';
import ArrowDownSlim from '@wh/svg-icons/ArrowDownSlim';
import ArrowUpSlim from '@wh/svg-icons/ArrowUpSlim';
import PropTypes from 'prop-types';

import './collapsible.scss';

class Collapsible extends Component {
    constructor(props) {
        super(props);
        this.linesToDisplay = this.props.linesToDisplay;
        this.sliderStylesShowed = { height: 'auto' };
        this.sliderStylesHidden = { height: '0' };
        this.state = {
            isOpen: false,
        };
        this.isTruncated = false;
    }

    componentDidMount() {
        this.setInitialData();
    }

    getSnapshotBeforeUpdate(prevProps, prevState) {
        if (prevState.isOpen === this.state.isOpen) return false;

        if (this.state.isOpen) this.showSlider();
        else this.hideSlider();

        return false;
    }

    componentDidUpdate(prevProp) {
        // Check content identity
        if (!isEqual(prevProp, this.props)) {
            // Check whether content should be collapsed or not
            if (this.state.isOpen) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ isOpen: false });
            }
            this.setInitialData();
        }
    }

    setStyleProperty = (node, styles) => {
        Object.keys(styles).forEach((key) => {
            node.style.setProperty(kebabCase(key), styles[key]);
        });
    };

    setNodeScrollHeight = (node) => {
        const height = node.scrollHeight;
        this.setStyleProperty(node, { height: `${height}px` });
        /**
         * Force re-flow to trigger transition.
         * Previous implementation used rAF which was nicer
         * bur because of bug (https://bugs.chromium.org/p/chromium/issues/detail?id=675795)
         * it works randomly.
         */
        node.getBoundingClientRect();
    };

    setInitialData = () => {
        this.initStyles();

        this.setStyleProperty(
            this.textWrapper,
            this.state.isOpen ? this.sliderStylesShowed : this.sliderStylesHidden,
        );
        this.toggleDots();
    };

    initStyles = () => {
        const shortTextHeight = this.shortText.clientHeight;
        const lineHeight = parseInt(global.getComputedStyle(this.textWrapper).lineHeight, 10);
        this.sliderStylesHidden.height = `${lineHeight * this.linesToDisplay}px`;

        if (shortTextHeight > lineHeight * this.linesToDisplay) {
            this.isTruncated = true;
            this.sliderStylesHidden.height = `${lineHeight * this.linesToDisplay}px`;
        } else {
            this.isTruncated = false;
            this.sliderStylesHidden.height = `${shortTextHeight}px`;
        }
    };

    handleTransitionEnd = (e) => {
        if (!e || e.propertyName !== 'height') return;
        if (!this.textWrapper) return;
        this.textWrapper.removeEventListener('transitionend', this.handleTransitionEnd);
        /*
         * After end of transition set height to auto.
         */
        this.setStyleProperty(
            this.textWrapper,
            this.state.isOpen ? this.sliderStylesShowed : this.sliderStylesHidden,
        );
        this.toggleDots();
    };

    hideSlider = () => {
        if (!this.textWrapper) return;
        this.textWrapper.addEventListener('transitionend', this.handleTransitionEnd);

        /*
         * Flip animation.
         * Set current height before animating.
         * This height will be starting point for transition.
         * In next tick set height to 0 to trigger transition.
         */
        this.setNodeScrollHeight(this.textWrapper);
        this.setStyleProperty(this.textWrapper, this.sliderStylesHidden);
    };

    showSlider = () => {
        if (!this.textWrapper) return;
        this.textWrapper.addEventListener('transitionend', this.handleTransitionEnd);
        this.setNodeScrollHeight(this.textWrapper);
    };

    /* eslint-disable react/no-access-state-in-setstate */
    toggleBox = () => {
        this.setState({ isOpen: !this.state.isOpen });
    };
    /* eslint-enable react/no-access-state-in-setstate */

    toggleDots = () => {
        if (!this.state.isOpen && this.isTruncated) {
            this.textWrapper.setAttribute('data-value', '...');
        } else {
            this.textWrapper.setAttribute('data-value', '');
        }
    };

    render() {
        return (
            <div styleName="wrapper">
                <div
                    ref={(el) => { this.textWrapper = el; }}
                    styleName="text_wrapper"
                >
                    <div ref={(el) => { this.shortText = el; }} styleName="short_text">
                        {this.props.shortText}
                    </div>
                    {this.props.children}
                </div>
                <hr className="-hr" />
                <div styleName="show_more">
                    <div
                        styleName="toggle_wrapper"
                        onClick={this.toggleBox}
                        role="button"
                        tabIndex="0"
                    >
                        <span>
                            {this.state.isOpen ? this.props.showLessText : this.props.showMoreText}
                        </span>
                        {this.state.isOpen ? <ArrowUpSlim /> : <ArrowDownSlim />}
                    </div>
                </div>
            </div>
        );
    }
}

Collapsible.defaultProps = {
    showLessText: 'Show less',
    showMoreText: 'Show more',
    linesToDisplay: 2,
    timeStamp: 0,
};

Collapsible.propTypes = {
    showLessText: PropTypes.string,
    showMoreText: PropTypes.string,
    linesToDisplay: PropTypes.number,
    children: PropTypes.node.isRequired,
    shortText: PropTypes.string.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    timeStamp: PropTypes.number,
};

export default Collapsible;
/* eslint-enable react/destructuring-assignment */
